import { useCallback } from 'react';

import {
  getDocs,
  query,
  collection,
  where,
  documentId,
  QueryDocumentSnapshot,
  CollectionReference,
  limit,
  orderBy,
  startAfter,
  getCountFromServer,
} from 'firebase/firestore';

import { useFirestore } from '@anyx/external-firebase';
import { reportAppError } from '@anyx/shared-utils';

import { createLatestNotificationListener, mergeNotificationsWithReadState } from '../firestore';
import { NotificationRaw, NotificationWithReadState } from '../types';

export interface NotificationsResult {
  notifications?: NotificationWithReadState[];
  lastVisible?: QueryDocumentSnapshot<NotificationRaw> | null;
}

export const useNotificationsPages = ({
  userId,
  accountId = null,
  pageSize,
}: {
  userId: string;
  accountId?: string | null;
  pageSize: number;
}) => {
  const { firestore } = useFirestore();
  const getNotifications = useCallback(
    async (
      lastVisibleDoc: QueryDocumentSnapshot<NotificationRaw> | null = null
    ): Promise<NotificationsResult> => {
      if (!userId || !firestore) return { notifications: [], lastVisible: null };

      try {
        const targetIds = [userId, accountId, 'global'].filter(Boolean);

        const q = query(
          collection(firestore, 'notifications') as CollectionReference<NotificationRaw>,
          where('type', 'in', ['user', 'account', 'global']),
          where('targetId', 'in', targetIds),
          orderBy('createdAt', 'desc'),
          limit(pageSize),
          ...(lastVisibleDoc ? [startAfter(lastVisibleDoc)] : [])
        );
        const notifSnap = await getDocs(q);

        const notifications = notifSnap.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        const readIds = notifications.map((n) => n.id);
        const readMap = new Map();

        if (readIds.length > 0) {
          const readSnap = await getDocs(
            query(
              collection(firestore, `users/${userId}/notificationStates`),
              where(documentId(), 'in', readIds)
            )
          );

          readSnap.forEach((doc) => readMap.set(doc.id, doc.data()['read']));
        }

        // Always return the full response, even if no read states found
        return {
          notifications: mergeNotificationsWithReadState(notifications, readMap),
          lastVisible: notifSnap.docs.at(-1) ?? null,
        };
      } catch (err) {
        reportAppError(err);
        throw new Error('Error getting notifications');
      }
    },
    [accountId, firestore, pageSize, userId]
  );

  const listenToLatestNotification = useCallback(
    (
      onUpdate: (data: { notification: NotificationWithReadState; readState: boolean }) => void
    ): {
      unsubscribeNotifications: () => void;
    } => {
      if (!userId || !firestore) {
        console.warn('[notifications] Skipped listener setup: no userId or firestore provided');
        return {
          unsubscribeNotifications: () => undefined,
        };
      }
      const targetIds = [userId, accountId, 'global'].filter(Boolean);

      try {
        const q = query(
          collection(firestore, 'notifications') as CollectionReference<NotificationRaw>,
          where('type', 'in', ['user', 'account', 'global']),
          where('targetId', 'in', targetIds),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const { unsubscribe } = createLatestNotificationListener(firestore, {
          query: q,
          userId,
          onUpdate,
        });

        return {
          unsubscribeNotifications: unsubscribe,
        };
      } catch (err) {
        console.error('[notifications] Failed to set up latest notification listener:', err);
        reportAppError(err);
        throw new Error('Error getting listening to notifications');
      }
    },
    [accountId, firestore, userId]
  );

  const getNotificationsCount = useCallback(async (): Promise<number> => {
    try {
      if (!firestore) {
        console.warn('[notifications] Skipped listener setup: no firestore provided');
        return 0;
      }
      const targetIds = [userId, accountId, 'global'].filter(Boolean);

      const notificationsQuery = query(
        collection(firestore, 'notifications'),
        where('type', 'in', ['user', 'account', 'global']),
        where('targetId', 'in', targetIds)
      );

      const snapshot = await getCountFromServer(notificationsQuery);
      return snapshot.data().count;
    } catch (error) {
      console.error('[notifications] Failed to get notifications count:', error);
      reportAppError(error);
      throw new Error('Error getting notifications count');
    }
  }, [accountId, firestore, userId]);

  return {
    getNotifications,
    listenToLatestNotification,
    getNotificationsCount,
  };
};
